import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Chip,
  Paper,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
} from "react-hook-form";
import useInstallmentSettingCreate from "./useInstallmentSettingCreate";

interface AutocompleteFieldProps {
  onChange: (value: any) => void;
  value: any;
}

export const InstallmentSettingCreate = () => {
  const {
    control,
    handleSubmit,
    onSubmit,
    isEdit,
    loading,
    companies,
    products,
    selectedProduct,
    handleGoBack,
    getAvailableInstallments,
    formatCurrency,
    errors,
  } = useInstallmentSettingCreate();

  const availableInstallmentNumbers = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  return (
    <Container maxWidth="lg">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          {isEdit ? "분할결제 설정 수정" : "분할결제 설정 생성"}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
        >
          목록으로 돌아가기
        </Button>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* 기본 정보 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={3}>
                  기본 정보
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="comId"
                      control={control}
                      rules={{ required: "도장을 선택해주세요" }}
                      render={({
                        field: { onChange, value },
                      }: {
                        field: AutocompleteFieldProps;
                      }) => (
                        <Autocomplete
                          options={companies}
                          getOptionLabel={(option) => option.comNm}
                          value={
                            companies.find((c) => c.comId === value) || null
                          }
                          onChange={(_, newValue) =>
                            onChange(newValue?.comId || 0)
                          }
                          disabled={isEdit}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="도장 선택"
                              error={!!errors.comId}
                              helperText={errors.comId?.message}
                              required
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="productId"
                      control={control}
                      rules={{ required: "상품을 선택해주세요" }}
                      render={({
                        field: { onChange, value },
                      }: {
                        field: AutocompleteFieldProps;
                      }) => (
                        <Autocomplete
                          options={products}
                          getOptionLabel={(option) =>
                            `${option.productNm} (${formatCurrency(
                              option.firstYearAmt
                            )})`
                          }
                          value={
                            products.find((p) => p.productId === value) || null
                          }
                          onChange={(_, newValue) =>
                            onChange(newValue?.productId || 0)
                          }
                          disabled={isEdit}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="상품 선택"
                              error={!!errors.productId}
                              helperText={errors.productId?.message}
                              required
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 분할결제 설정 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={3}>
                  분할결제 설정
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Controller
                      name="allowInstallment"
                      control={control}
                      render={({ field }: { field: any }) => (
                        <FormControl>
                          <FormLabel component="legend">
                            분할결제 허용 여부
                          </FormLabel>
                          <RadioGroup {...field} row sx={{ mt: 1 }}>
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="허용"
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="비허용"
                            />
                          </RadioGroup>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="maxInstallmentCount"
                      control={control}
                      rules={{
                        required: "최대 분할 횟수를 입력해주세요",
                        min: {
                          value: 2,
                          message: "최소 2회 이상이어야 합니다",
                        },
                        max: {
                          value: 12,
                          message: "최대 12회까지만 가능합니다",
                        },
                      }}
                      render={({ field }: { field: any }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="최대 분할 횟수"
                          type="number"
                          error={!!errors.maxInstallmentCount}
                          helperText={errors.maxInstallmentCount?.message}
                          inputProps={{ min: 2, max: 12 }}
                          required
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="minInstallmentAmount"
                      control={control}
                      rules={{
                        required: "최소 분할 금액을 입력해주세요",
                        min: { value: 1, message: "1 이상이어야 합니다" },
                      }}
                      render={({ field }: { field: any }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="최소 분할 금액 ($)"
                          type="number"
                          error={!!errors.minInstallmentAmount}
                          helperText={errors.minInstallmentAmount?.message}
                          inputProps={{ min: 1 }}
                          required
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="availableInstallments"
                      control={control}
                      rules={{
                        required: "허용할 분할 옵션을 선택해주세요",
                        validate: (value: number[]) =>
                          value.length > 0 || "최소 하나의 옵션을 선택해주세요",
                      }}
                      render={({
                        field: { onChange, value },
                      }: {
                        field: {
                          onChange: (value: number[]) => void;
                          value: number[];
                        };
                      }) => (
                        <Box>
                          <Typography
                            variant="body1"
                            fontWeight="medium"
                            mb={2}
                          >
                            허용 분할 옵션 선택
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {availableInstallmentNumbers.map((num) => (
                              <Chip
                                key={num}
                                label={`${num}회`}
                                clickable
                                color={
                                  value.includes(num) ? "primary" : "default"
                                }
                                onClick={() => {
                                  const newValue = value.includes(num)
                                    ? value.filter((v: number) => v !== num)
                                    : [...value, num].sort((a, b) => a - b);
                                  onChange(newValue);
                                }}
                              />
                            ))}
                          </Box>
                          {errors.availableInstallments && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 1, display: "block" }}
                            >
                              {errors.availableInstallments.message}
                            </Typography>
                          )}
                        </Box>
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 분할 옵션 미리보기 */}
          {selectedProduct && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" mb={3}>
                    분할 옵션 미리보기
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    상품 금액: {formatCurrency(selectedProduct.firstYearAmt)}
                  </Alert>
                  {getAvailableInstallments().length > 0 ? (
                    <Grid container spacing={2}>
                      {getAvailableInstallments().map((option) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          lg={2}
                          key={option.installments}
                        >
                          <Paper
                            elevation={1}
                            sx={{
                              p: 2,
                              textAlign: "center",
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            <Typography variant="h6" color="primary">
                              {option.installments}회 분할
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              월 {formatCurrency(option.installmentAmount)}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              총 {formatCurrency(option.totalAmount)}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Alert severity="warning">
                      선택된 분할 옵션이 없습니다. 위에서 분할 옵션을
                      선택해주세요.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* 저장 버튼 */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={handleGoBack}
                disabled={loading}
              >
                취소
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  loading ? <CircularProgress size={20} /> : <SaveIcon />
                }
                disabled={loading}
              >
                {loading ? "처리 중..." : isEdit ? "수정하기" : "생성하기"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default InstallmentSettingCreate;
